/**
 * Absolute session-duration cap — port of legacy `SessionDurationService`
 * (`raptor-shell-ui/.../core/services/session-duration.service.ts`).
 *
 * The cap is anchored to the app login time (`login.time`, per-tab
 * sessionStorage), NOT any IdP token claim, so it resets on a genuine re-login
 * (silent SSO included) and cannot loop the way an `auth_time` anchor did.
 *
 * A tab opened directly by URL has no `login.time`: on start it asks peer tabs
 * for the value over the `session_cap_channel` BroadcastChannel and adopts the
 * EARLIEST one so every open tab caps from the same anchor; if no peer answers
 * within `PEER_WAIT_MS` it seeds its own start (the inherent limit of
 * sessionStorage once every tab is closed).
 *
 * On the 60s warning it drives `SessionCapModal`; on expiry (or the modal's
 * "Sign out now") it performs a full logout via `authModule.logout()`, which
 * broadcasts on `logout_channel` so peer tabs follow (legacy `logOut(true)`).
 */
import { authModule } from './auth-module';
import { getTracker } from '@/telemetry/tracker';
import { envNumber } from '@/config/env-flags';

/** Legacy `SESSION_STORAGE.LOGIN_TIME`. */
const LOGIN_TIME_KEY = 'login.time';
const WARNING_BEFORE_EXPIRY_MS = 60_000;
const DEFAULT_MAX_DURATION_HOURS = 10;
const MS_PER_HOUR = 60 * 60 * 1000;
const CAP_CHANNEL = 'session_cap_channel';
const MSG_REQUEST_LOGIN_TIME = 'cap:requestLoginTime';
const MSG_LOGIN_TIME = 'cap:loginTime';
const PEER_WAIT_MS = 1000;
const COUNTDOWN_INTERVAL_MS = 1000;

export type SessionCapStatus = 'inactive' | 'warned' | 'expired';

export interface SessionCapState {
  status: SessionCapStatus;
  /** Milliseconds left until forced logout while status === 'warned'. */
  remainingMs: number;
}

/**
 * Write the session-cap anchor. Call on every genuine (interactive) login —
 * legacy `login.component` writes `login.time` on the login button click.
 */
export function markLoginTime(): void {
  try {
    sessionStorage.setItem(LOGIN_TIME_KEY, new Date().toString());
  } catch {
    /* sessionStorage unavailable (private mode / blocked) */
  }
}

function maxDurationMs(): number {
  const hours = envNumber(
    import.meta.env.VITE_MAX_SESSION_DURATION_HOURS,
    DEFAULT_MAX_DURATION_HOURS,
  );
  return (hours > 0 ? hours : DEFAULT_MAX_DURATION_HOURS) * MS_PER_HOUR;
}

interface CapMessage {
  type: typeof MSG_REQUEST_LOGIN_TIME | typeof MSG_LOGIN_TIME;
  value?: number;
}

class SessionDurationManager {
  private running = false;
  private warningTimer: number | null = null;
  private logoutTimer: number | null = null;
  private peerWaitTimer: number | null = null;
  private countdownTimer: number | null = null;
  private channel: BroadcastChannel | null = null;
  /** "OK/Dismiss" acknowledged — hide the modal, but expiry still logs out. */
  private dismissed = false;

  private state: SessionCapState = { status: 'inactive', remainingMs: 0 };
  private listeners = new Set<(s: SessionCapState) => void>();

  start(): void {
    if (this.running) return;
    this.running = true;
    // Fresh session lifecycle: clear any "dismissed" carried over from a prior
    // session in the same JS context (SPA re-login without a full page reload),
    // so the new session's cap warning is not silently suppressed.
    this.dismissed = false;
    this.setupChannel();
    this.scheduleTimers();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.clearAllTimers();
    if (this.channel) {
      try {
        this.channel.close();
      } catch {
        /* noop */
      }
      this.channel = null;
    }
    this.setState({ status: 'inactive', remainingMs: 0 });
  }

  /** "OK" on the warning modal — hides it, but expiry still forces logout. */
  dismiss(): void {
    if (this.state.status !== 'warned') return;
    getTracker().event('session_cap_warning_dismissed');
    this.stopCountdown();
    this.dismissed = true;
    this.setState({ status: 'inactive', remainingMs: 0 });
  }

  /** "Sign out now" on the warning modal. */
  logoutNow(): void {
    getTracker().event('session_cap_manual_logout');
    this.forceLogout();
  }

  getState(): SessionCapState {
    return this.state;
  }

  subscribe(cb: (s: SessionCapState) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  // -------------------------------------------------------- internals --

  /**
   * Arm warning/logout timers from `login.time`. With no local `login.time`
   * (URL-opened tab), ask peers and seed our own after a short grace period.
   */
  private scheduleTimers(): void {
    this.clearScheduleTimers();
    const start = this.getLoginTimeMs();
    if (start === null) {
      this.requestLoginTimeFromPeers();
      this.armPeerWaitFallback();
      return;
    }
    this.cancelPeerWait();
    this.armTimers(start);
  }

  private armTimers(start: number): void {
    const remaining = Math.max(0, start + maxDurationMs() - Date.now());
    if (remaining <= 0) {
      this.expire();
      return;
    }
    if (remaining > WARNING_BEFORE_EXPIRY_MS) {
      this.warningTimer = window.setTimeout(() => {
        this.warningTimer = null;
        if (this.getLoginTimeMs() !== null) this.enterWarning();
      }, remaining - WARNING_BEFORE_EXPIRY_MS);
    } else {
      this.enterWarning();
    }
    this.logoutTimer = window.setTimeout(() => {
      this.logoutTimer = null;
      if (this.isExpired()) this.expire();
    }, remaining);
  }

  private enterWarning(): void {
    if (this.dismissed || this.state.status === 'expired') return;
    this.setState({
      status: 'warned',
      remainingMs: this.remainingMs() ?? WARNING_BEFORE_EXPIRY_MS,
    });
    getTracker().event('session_cap_warning_shown');
    this.stopCountdown();
    this.countdownTimer = window.setInterval(() => {
      const rem = this.remainingMs() ?? 0;
      if (rem <= 0) {
        this.stopCountdown();
        return;
      }
      this.setState({ status: 'warned', remainingMs: rem });
    }, COUNTDOWN_INTERVAL_MS);
  }

  private expire(): void {
    if (this.state.status === 'expired') return;
    this.clearAllTimers();
    this.setState({ status: 'expired', remainingMs: 0 });
    getTracker().event('session_cap_expired');
    this.forceLogout();
  }

  private forceLogout(): void {
    this.clearAllTimers();
    // Full logout — broadcasts on logout_channel so peer tabs follow, clears
    // storage, then redirects to the AAD sign-out (legacy `logOut(true)`).
    authModule.logout();
  }

  private isExpired(): boolean {
    const start = this.getLoginTimeMs();
    if (start === null) return false;
    return Date.now() - start >= maxDurationMs();
  }

  private remainingMs(): number | null {
    const start = this.getLoginTimeMs();
    if (start === null) return null;
    return Math.max(0, start + maxDurationMs() - Date.now());
  }

  private getLoginTimeMs(): number | null {
    try {
      const raw = sessionStorage.getItem(LOGIN_TIME_KEY);
      if (!raw) return null;
      const ms = new Date(raw).getTime();
      return Number.isFinite(ms) && ms > 0 ? ms : null;
    } catch {
      return null;
    }
  }

  private setLoginTimeMs(ms: number): void {
    try {
      sessionStorage.setItem(LOGIN_TIME_KEY, new Date(ms).toString());
    } catch {
      /* noop */
    }
  }

  private setupChannel(): void {
    try {
      if (typeof BroadcastChannel === 'undefined') return;
      this.channel = new BroadcastChannel(CAP_CHANNEL);
      this.channel.onmessage = (event: MessageEvent) => {
        const data = (event?.data ?? {}) as CapMessage;
        if (data.type === MSG_REQUEST_LOGIN_TIME) {
          const start = this.getLoginTimeMs();
          if (start !== null) this.post({ type: MSG_LOGIN_TIME, value: start });
        } else if (data.type === MSG_LOGIN_TIME && typeof data.value === 'number') {
          this.adoptPeerLoginTime(data.value);
        }
      };
    } catch {
      this.channel = null;
    }
  }

  /** Adopt the EARLIEST login.time so every open tab caps from one anchor. */
  private adoptPeerLoginTime(peerStart: number): void {
    if (!(Number.isFinite(peerStart) && peerStart > 0)) return;
    const current = this.getLoginTimeMs();
    if (current === null || peerStart < current) {
      this.setLoginTimeMs(peerStart);
      this.cancelPeerWait();
      this.clearScheduleTimers();
      this.armTimers(peerStart);
    }
  }

  private requestLoginTimeFromPeers(): void {
    this.post({ type: MSG_REQUEST_LOGIN_TIME });
  }

  private armPeerWaitFallback(): void {
    this.cancelPeerWait();
    this.peerWaitTimer = window.setTimeout(() => {
      this.peerWaitTimer = null;
      if (this.getLoginTimeMs() === null) {
        const now = Date.now();
        this.setLoginTimeMs(now);

        this.armTimers(now);
      }
    }, PEER_WAIT_MS);
  }

  private cancelPeerWait(): void {
    if (this.peerWaitTimer) {
      clearTimeout(this.peerWaitTimer);
      this.peerWaitTimer = null;
    }
  }

  private post(msg: CapMessage): void {
    try {
      this.channel?.postMessage(msg);
    } catch {
      /* noop */
    }
  }

  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private clearScheduleTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  private clearAllTimers(): void {
    this.clearScheduleTimers();
    this.cancelPeerWait();
    this.stopCountdown();
  }

  private setState(next: SessionCapState): void {
    this.state = next;
    for (const cb of this.listeners) cb(next);
  }
}

export const sessionDurationManager = new SessionDurationManager();
